import { cleanupIndexedDb, DEFAULT_SESSION_STORAGE_KEY, initWebLogger } from 'ray-logger-core'
import { UploadManager } from 'ray-logger-report'
import { refreshManager } from '@rayvision/refresh-manager'
import { REFRESH_KEYS } from './refresh-manager/constants'

let controller = null
let uploadManager = null
let loggerRefreshUnsubscribe = null

const PROD_HOSTS = [
  'admin.renderbus.com',
  'admin.foxrenderfarm.com',
  'admin.dayancloud.com',
  'admin.avicacloud.com',
]

const LOGGER_BASE_URL = {
  test: 'http://10.51.154.48:8443',
  prod: 'https://logger.rayvision.com:10443',
}

const LOGGER_ENABLED_STORAGE_KEY = 'enable_web_logger'

function parseLoggerEnabledValue(value) {
  if (typeof value !== 'string') return null

  if (value === '1' || value === 'true') return true
  if (value === '0' || value === 'false') return false

  return null
}

export function syncLoggerEnabledFromUrl() {
  if (typeof window === 'undefined' || !window.localStorage) return

  const searchParams = new URLSearchParams(window.location.search)
  const nextEnabled = parseLoggerEnabledValue(searchParams.get(LOGGER_ENABLED_STORAGE_KEY))

  if (nextEnabled === null) return

  if (nextEnabled === true) {
    window.localStorage.setItem(LOGGER_ENABLED_STORAGE_KEY, 'true')
  }

  if (nextEnabled === false) {
    window.localStorage.setItem(LOGGER_ENABLED_STORAGE_KEY, 'false')
  }

  searchParams.delete(LOGGER_ENABLED_STORAGE_KEY)

  const nextSearch = searchParams.toString()
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`

  window.history.replaceState(window.history.state, '', nextUrl)
}

export function isLoggerEnabled() {
  if (typeof window === 'undefined' || !window.localStorage) return false

  return parseLoggerEnabledValue(window.localStorage.getItem(LOGGER_ENABLED_STORAGE_KEY)) === true
}

export function getLoggerEnv() {
  if (typeof window === 'undefined') return 'test'

  return PROD_HOSTS.includes(window.location.host) ? 'prod' : 'test'
}

export function getLoggerBaseUrl() {
  return LOGGER_BASE_URL[getLoggerEnv()]
}

export function getBusinessLine() {
  return 'renderbus'
}

function normalizeUserId(userId) {
  const normalizedUserId = Number(userId)

  if (!Number.isFinite(normalizedUserId)) return 0

  return Math.trunc(normalizedUserId)
}

function normalizeRemark(remark) {
  if (typeof remark === 'string' && remark.trim()) {
    return remark.trim()
  }

  return '后台管理手动上报'
}

function normalizeUpdateTime(value) {
  const updateTime = Number(value)
  return Number.isFinite(updateTime) ? updateTime : 0
}

async function invokeDestroy(instance) {
  if (!instance || typeof instance.destroy !== 'function') {
    return
  }

  await instance.destroy()
}

export async function destroyLoggerInstances() {
  const currentUploadManager = uploadManager
  const currentController = controller

  uploadManager = null
  controller = null

  await invokeDestroy(currentUploadManager)
  await invokeDestroy(currentController)
}

function clearLoggerSessionStorage() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.removeItem(DEFAULT_SESSION_STORAGE_KEY)
  } catch (error) {
    console.warn('[logger-refresh] clear session storage failed', error)
  }
}

export async function clearLoggerStorageAndInstance() {
  await destroyLoggerInstances()
  clearLoggerSessionStorage()
  const cleanupResult = await cleanupIndexedDb({
    ttlMs: 0,
  })
  console.info('[logger-refresh] clear action applied', cleanupResult)
}

export function ensureLoggerRefreshSubscription() {
  if (loggerRefreshUnsubscribe) {
    return loggerRefreshUnsubscribe
  }

  loggerRefreshUnsubscribe = refreshManager.register(REFRESH_KEYS.LOGGER, async (nextSnapshot, previousSnapshot) => {
    const nextValue = nextSnapshot && nextSnapshot.value ? nextSnapshot.value : {}
    const previousValue = previousSnapshot && previousSnapshot.value ? previousSnapshot.value : {}
    const nextUpdateTime = normalizeUpdateTime(nextValue.updateTime)
    const previousUpdateTime = normalizeUpdateTime(previousValue.updateTime)

    if (nextUpdateTime <= previousUpdateTime) {
      return
    }

    if (nextValue.action === 'clear') {
      await clearLoggerStorageAndInstance()
    }
  })

  return loggerRefreshUnsubscribe
}

export function ensureLoggerReady() {
  if (!isLoggerEnabled()) return null
  if (uploadManager) return uploadManager

  controller = initWebLogger({
    disallowRecordRequestHeaders: [],
    disallowRecordResponseHeaders: [],
    rrwebOptions: {
      recordCanvas: false,
    },
    onFlushSuccess(result) {
      console.info('[web-logger] flush success', result)
    },
    onFlushError(error) {
      console.error('[web-logger] flush failed', error)
    },
  })

  uploadManager = new UploadManager({
    baseUrl: getLoggerBaseUrl(),
    controller,
  })

  return uploadManager
}

export async function uploadRecentLogs({ userId, remark, sinceMs } = {}) {
  const manager = ensureLoggerReady()

  if (!manager) return null

  return manager.uploadSince({
    businessLine: getBusinessLine(),
    userId: normalizeUserId(userId),
    remark: normalizeRemark(remark),
    sinceMs: Number.isFinite(sinceMs) ? sinceMs : Date.now() - 5 * 60 * 1000,
  })
}
